import React, { useEffect, useRef, useState } from "react";

/**
 * Renders an animated overlay over a target DOM element so the AI can show
 * users where things are. Listens for two window events:
 *   - "gw-ai-highlight"  { detail: { selector?, sectionId?, label?, durationMs? } }
 *   - "gw-ai-highlight-clear"
 *
 * Repositions on resize / scroll so the highlight stays glued to the target
 * while the user moves around the page.
 */
export default function GatewayAIHighlight() {
  const [target, setTarget] = useState(null); // { rect, label }
  const timeoutRef = useRef(null);
  const targetElRef = useRef(null);

  useEffect(() => {
    const resolve = (selector) => {
      if (!selector) return null;
      try {
        return document.querySelector(selector);
      } catch {
        return null;
      }
    };

    const updatePosition = () => {
      const el = targetElRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      setTarget((prev) =>
        prev
          ? {
              ...prev,
              rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
            }
          : prev
      );
    };

    const handleHighlight = (e) => {
      const detail = e.detail || {};
      const selector = detail.selector;
      const label = detail.label || null;
      const duration = Number.isFinite(detail.durationMs) ? detail.durationMs : 6000;

      const el = resolve(selector);
      if (!el) {
        targetElRef.current = null;
        setTarget(null);
        return;
      }
      targetElRef.current = el;
      // Scroll target into view if needed.
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      } catch {}
      // Defer measure to after scroll settles a bit.
      requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        setTarget({
          rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
          label,
        });
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (duration > 0) {
        timeoutRef.current = setTimeout(() => {
          targetElRef.current = null;
          setTarget(null);
          timeoutRef.current = null;
        }, duration);
      }
    };
    const handleClear = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      targetElRef.current = null;
      setTarget(null);
    };

    window.addEventListener("gw-ai-highlight", handleHighlight);
    window.addEventListener("gw-ai-highlight-clear", handleClear);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("gw-ai-highlight", handleHighlight);
      window.removeEventListener("gw-ai-highlight-clear", handleClear);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  if (!target?.rect) return null;
  const { top, left, width, height } = target.rect;
  return (
    <div
      className="gwAIHighlightOverlay"
      style={{
        top: `${top - 4}px`,
        left: `${left - 4}px`,
        width: `${width + 8}px`,
        height: `${height + 8}px`,
      }}
      role="presentation"
    >
      {target.label && <div className="gwAIHighlightLabel">{target.label}</div>}
    </div>
  );
}
